import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Download, ExternalLink, FileWarning, Layers, MessageSquare, Trash2, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { BotonAccion, ModalComentario, ModalNuevaVersion, ModalEliminar } from "@/components/AccionesDocumento";
import { ListaComentarios, type ObservacionResumen } from "@/components/ComentariosDocumento";

/**
 * Visor de documento embebido para Calidad. Layout split:
 *  - Izquierda: iframe con la URL presignada de MinIO (renderizado nativo PDF).
 *  - Derecha: tabs "Versiones" (timeline append-only) y "Comentarios"
 *    (observaciones de la versión vigente).
 *
 * Fase B: además de consultar, permite comentar, subir una nueva versión y
 * retirar el documento. Qué acciones se ofrecen lo decide el backend en
 * `/acciones` según el estado del trámite: la pantalla pregunta, no adivina, y
 * el backend vuelve a validarlo al ejecutar.
 */

interface Props {
  documentoId: string;
  onClose: () => void;
}

interface DocumentoResumen {
  documentoId: string;
  versionVigenteId: string;
  numeroVersionVigente: number;
  tipoCodigo: string;
  tipoNombre: string;
  contextoTipo: string;
  contextoId: string;
  nombreArchivo: string;
  sizeBytes: number;
  sha256: string;
  estatusVersion: string;
  antivirusEstado: string;
  esCopiaCertificadaOriginal: boolean;
  fechaCarga: string;
  vigenciaFin: string | null;
}

interface VersionResumen {
  id: string;
  documentoId: string;
  numeroVersion: number;
  nombreArchivo: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  estatus: "CARGADA" | "OBSERVADA" | "ACEPTADA" | "RECHAZADA" | "SUSTITUIDA";
  antivirusEstado: "PENDIENTE" | "LIMPIO" | "INFECTADO" | "ERROR_ESCANEO";
  esCopiaCertificadaOriginal: boolean;
  vigenciaInicio: string | null;
  vigenciaFin: string | null;
  cargadoPor: string | null;
  fechaCarga: string;
  decididoPor: string | null;
  fechaDecision: string | null;
  motivoDecision: string | null;
}

interface AccionesDocumento {
  puedeDescargar: boolean;
  puedeNuevaVersion: boolean;
  puedeComentar: boolean;
  puedeEliminar: boolean;
  esBorrador: boolean;
  tramiteCerrado: boolean;
  estadoTramite: string;
}

interface UrlDescargaTemporal {
  url: string;
  expiraEn: string;
  nombreArchivo: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
}

export function DocumentoPreviewModal({ documentoId, onClose }: Props) {
  const [tab, setTab] = useState<"versiones" | "comentarios">("versiones");
  const [versionSeleccionada, setVersionSeleccionada] = useState<string | null>(null);
  const [accionAbierta, setAccionAbierta] = useState<"comentar" | "version" | "eliminar" | null>(null);
  const qc = useQueryClient();

  /** Tras cualquier cambio, releer lo que pudo moverse: versiones, comentarios y permisos. */
  const refrescar = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["doc-resumen", documentoId] }),
      qc.invalidateQueries({ queryKey: ["doc-versiones", documentoId] }),
      qc.invalidateQueries({ queryKey: ["doc-acciones", documentoId] }),
      qc.invalidateQueries({ queryKey: ["doc-observaciones"] }),
      qc.invalidateQueries({ queryKey: ["documentos-contexto"] }),
    ]);
    setVersionSeleccionada(null);
  };

  const qDoc = useQuery<DocumentoResumen>({
    queryKey: ["doc-resumen", documentoId],
    queryFn: async () => (await api.get<DocumentoResumen>(`/api/documentos/${documentoId}`)).data,
  });

  // Cuando llega el resumen, fija la versión vigente como seleccionada por defecto.
  const versionInicial = qDoc.data?.versionVigenteId ?? null;
  const versionActiva = versionSeleccionada ?? versionInicial;

  const qUrl = useQuery<UrlDescargaTemporal>({
    queryKey: ["doc-preview-url", versionActiva],
    enabled: !!versionActiva,
    queryFn: async () => {
      const { data } = await api.post<UrlDescargaTemporal>(
        `/api/documentos/versiones/${versionActiva}/descarga`,
        { motivo: "Previsualización en detalle de RL" },
      );
      return data;
    },
    staleTime: 4 * 60 * 1000,
    refetchInterval: 4 * 60 * 1000,
  });

  const qVersiones = useQuery<VersionResumen[]>({
    queryKey: ["doc-versiones", documentoId],
    queryFn: async () => (await api.get<VersionResumen[]>(`/api/documentos/${documentoId}/versiones`)).data,
  });

  const qAcciones = useQuery<AccionesDocumento>({
    queryKey: ["doc-acciones", documentoId],
    queryFn: async () =>
      (await api.get<AccionesDocumento>(`/api/documentos/${documentoId}/acciones`)).data,
  });

  const qObservaciones = useQuery<ObservacionResumen[]>({
    queryKey: ["doc-observaciones", versionActiva],
    enabled: !!versionActiva,
    queryFn: async () =>
      (await api.get<ObservacionResumen[]>(`/api/documentos/versiones/${versionActiva}/observaciones`)).data,
  });

  const contentTypeActivo =
    qVersiones.data?.find((v) => v.id === versionActiva)?.contentType ?? qUrl.data?.contentType ?? "";
  const nombreArchivo = qDoc.data?.nombreArchivo ?? "Documento";
  const versionVigenteId = qDoc.data?.versionVigenteId ?? "";
  const esPdf = contentTypeActivo.toLowerCase().includes("pdf");
  const esImg = contentTypeActivo.toLowerCase().startsWith("image/");
  const previewable = esPdf || esImg;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2">
      <div className="flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-fg">{nombreArchivo}</h3>
            <p className="text-xs text-fg-subtle">Vista de previsualización (solo lectura)</p>
          </div>
          <div className="flex items-center gap-2">
            {qUrl.data && (
              <a
                href={qUrl.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
              >
                <ExternalLink className="h-3 w-3" /> Abrir en nueva pestaña
              </a>
            )}
            {qUrl.data && (
              <a
                href={qUrl.data.url}
                download={qUrl.data.nombreArchivo}
                className="inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
              >
                <Download className="h-3 w-3" /> Descargar
              </a>
            )}
            <button onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden sm:flex-row">
          {/* Visor */}
          <div className="flex min-h-[40vh] flex-1 items-center justify-center bg-surface-2 sm:min-h-0">
            {qUrl.isLoading && <span className="text-sm text-fg-subtle">Cargando previsualización…</span>}
            {qUrl.error && (
              <div className="m-4 max-w-md rounded border border-estado-warn/25 bg-estado-warn-bg p-3 text-sm text-estado-warn">
                <FileWarning className="mb-1 h-4 w-4" />
                No fue posible cargar la previsualización. Usa “Abrir en nueva pestaña”.
              </div>
            )}
            {qUrl.data && previewable && esPdf && (
              <iframe
                key={qUrl.data.url}
                title={nombreArchivo}
                src={qUrl.data.url}
                className="h-full w-full"
              />
            )}
            {qUrl.data && previewable && esImg && (
              <img src={qUrl.data.url} alt={nombreArchivo} className="max-h-full max-w-full object-contain" />
            )}
            {qUrl.data && !previewable && (
              <div className="m-4 max-w-md rounded border bg-surface p-4 text-sm text-fg-muted">
                Este tipo de archivo ({contentTypeActivo || "desconocido"}) no se puede previsualizar embebido.
                Usa “Descargar” o “Abrir en nueva pestaña”.
              </div>
            )}
          </div>

          {/* Panel lateral */}
          <div className="flex w-full flex-col border-t sm:w-[420px] sm:border-l sm:border-t-0">
            <div className="flex border-b text-sm">
              <TabBtn active={tab === "versiones"} onClick={() => setTab("versiones")} icon={<Layers className="h-3.5 w-3.5" />}>
                Versiones {qVersiones.data ? `(${qVersiones.data.length})` : ""}
              </TabBtn>
              <TabBtn active={tab === "comentarios"} onClick={() => setTab("comentarios")} icon={<MessageSquare className="h-3.5 w-3.5" />}>
                Comentarios {qObservaciones.data ? `(${qObservaciones.data.length})` : ""}
              </TabBtn>
            </div>
            {/* Acciones: solo las que el backend autoriza para el estado del trámite */}
            {qAcciones.data && (
              <div className="flex flex-wrap items-center gap-2 border-b bg-surface-2 px-3 py-2">
                {qAcciones.data.puedeComentar && (
                  <BotonAccion icon={<MessageSquare className="h-3.5 w-3.5" />}
                               onClick={() => setAccionAbierta("comentar")}>
                    Comentar
                  </BotonAccion>
                )}
                {qAcciones.data.puedeNuevaVersion && (
                  <BotonAccion icon={<Upload className="h-3.5 w-3.5" />}
                               onClick={() => setAccionAbierta("version")}>
                    Nueva versión
                  </BotonAccion>
                )}
                {qAcciones.data.puedeEliminar && (
                  <BotonAccion peligro icon={<Trash2 className="h-3.5 w-3.5" />}
                               onClick={() => setAccionAbierta("eliminar")}>
                    Eliminar
                  </BotonAccion>
                )}
                {qAcciones.data.tramiteCerrado && (
                  <span className="text-[11px] text-fg-subtle">
                    Trámite cerrado ({qAcciones.data.estadoTramite}): el expediente ya no se modifica.
                  </span>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {tab === "versiones" && (
                <PanelVersiones
                  versiones={qVersiones.data ?? []}
                  loading={qVersiones.isLoading}
                  versionVigenteId={versionVigenteId}
                  seleccionada={versionActiva ?? ""}
                  onSeleccionar={setVersionSeleccionada}
                />
              )}
              {tab === "comentarios" && (
                <ListaComentarios
                  observaciones={qObservaciones.data ?? []}
                  loading={qObservaciones.isLoading}
                  vacio="Sin comentarios sobre esta versión."
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {accionAbierta === "comentar" && versionActiva && (
        <ModalComentario
          versionId={versionActiva}
          onClose={() => { setAccionAbierta(null); setTab("comentarios"); }}
          onHecho={refrescar}
        />
      )}
      {accionAbierta === "version" && (
        <ModalNuevaVersion
          documentoId={documentoId}
          onClose={() => setAccionAbierta(null)}
          onHecho={async () => { setAccionAbierta(null); await refrescar(); }}
        />
      )}
      {accionAbierta === "eliminar" && (
        <ModalEliminar
          documentoId={documentoId}
          nombreArchivo={nombreArchivo}
          esBorrador={qAcciones.data?.esBorrador ?? false}
          onClose={() => setAccionAbierta(null)}
          onHecho={async () => { setAccionAbierta(null); onClose(); }}
        />
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2 text-xs font-medium ${
        active ? "border-primary-strong text-primary-strong" : "border-transparent text-fg-muted hover:bg-surface-2"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function PanelVersiones({
  versiones,
  loading,
  versionVigenteId,
  seleccionada,
  onSeleccionar,
}: {
  versiones: VersionResumen[];
  loading: boolean;
  versionVigenteId: string;
  seleccionada: string;
  onSeleccionar: (id: string) => void;
}) {
  if (loading) return <Empty>Cargando…</Empty>;
  if (versiones.length === 0) return <Empty>Sin versiones registradas.</Empty>;
  return (
    <ul className="divide-y">
      {versiones.map((v) => {
        const esVigente = v.id === versionVigenteId;
        const esSel = v.id === seleccionada;
        return (
          <li
            key={v.id}
            className={`cursor-pointer p-3 hover:bg-estado-info-bg/40 ${esSel ? "bg-estado-info-bg" : ""}`}
            onClick={() => onSeleccionar(v.id)}
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-fg">v{v.numeroVersion}</span>
              <div className="flex items-center gap-1">
                {esVigente && <Tag color="emerald">vigente</Tag>}
                <Tag color={colorPorEstatusVersion(v.estatus)}>{v.estatus.toLowerCase()}</Tag>
              </div>
            </div>
            <div className="mt-1 truncate text-xs text-fg-muted">{v.nombreArchivo}</div>
            <div className="mt-1 text-[11px] text-fg-subtle">
              {formatFecha(v.fechaCarga)} · {formatBytes(v.sizeBytes)}
            </div>
            {v.motivoDecision && (
              <div className="mt-1 text-[11px] italic text-fg-subtle">“{v.motivoDecision}”</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-center text-xs text-fg-subtle">{children}</div>;
}

function Tag({ color, children }: { color: "emerald" | "amber" | "rose" | "gray" | "blue"; children: React.ReactNode }) {
  const map = {
    emerald: "bg-estado-ok-bg text-estado-ok",
    amber: "bg-estado-warn-bg text-estado-warn",
    rose: "bg-estado-err-bg text-estado-err",
    gray: "bg-surface-2 text-fg-muted",
    blue: "bg-estado-info-bg text-estado-info",
  } as const;
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${map[color]}`}>{children}</span>;
}

function colorPorEstatusVersion(s: VersionResumen["estatus"]): "emerald" | "amber" | "rose" | "gray" | "blue" {
  if (s === "ACEPTADA") return "emerald";
  if (s === "OBSERVADA") return "amber";
  if (s === "RECHAZADA") return "rose";
  if (s === "SUSTITUIDA") return "gray";
  return "blue"; // CARGADA
}

function formatFecha(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("es-MX", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
